import { useState, useEffect } from "react";
import api from "api/api";
import { useNavigate, Link } from "react-router-dom";

// Material UI Components
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

// Mui icons
import LineAxisOutlinedIcon from "@mui/icons-material/LineAxisOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Props Validations
import PropTypes from "prop-types";

// Import Components
import FiltersHeaderLabel from "../components/common/FiltersHeaderLabel";
import Header from "../components/common/Header";
import FragrancePerformanceTab from "../components/expressCLT/overallAnalysis/FragrancePerformanceTab";
import AditionalAttributeTab from "components/expressCLT/aditionalAnalysis/AditionalAttributeTab";
import Loader from "../components/common/Loader";
import MainNavigation from "../components/common/MainNavigation";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { loadFragranceMetricsRemovedKpiListState } from "store/features/analysisKpiSlice";
import { showNotification } from "store/features/notificationSlice";

function CustomTabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

function FragranceMetricsExpressCLT({ headerTitle = "" }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;
    const navigationAvailability = useSelector((state) => state.studyDataStore.navigationAvailability);

    const [selectedTab, setSelectedTab] = useState(0);
    const [additionalAnalysisData, setAdditionalAnalysisData] = useState([]);
    const [overallAnalysisData, setOverallAnalysisData] = useState([]);
    const [isAdditionalAnalysisDataLoading, setIsAdditionalAnalysisDataLoading] = useState(false);
    const [isOverallAnalysisDataLoading, setIsOverallAnalysisDataLoading] = useState(false);
    const [showFragranceLikingNote, setShowFragranceLikingNote] = useState(false);
    const currentAnalysisResult = useSelector((state) => state.analysisResultsStore.fragranceMetricsAnalysisResult);

    const btnArr = ["preferenceAnalysis", "fragranceAssociation", "agreementkpi", "productPreferenceAnalysisExpressCLT"];
    const page = {
        0: "overall-analysis",
        1: "additional-analysis",
    };

    const [btnDisabled, setBtnDisabled] = useState({});

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    // Clear the removed KPI data from the Redux store on page change to reset the default view.
    // Remove the code below if you want to retain the removed variables.
    useEffect(() => {
        return () => {
            dispatch(loadFragranceMetricsRemovedKpiListState([]));
        };
    }, []);

    const handleChange = (event, newValue) => {
        console.log("event", event, newValue);
        setSelectedTab(newValue);
    };

    useEffect(() => {
        let _selectedStudies = selectedStudy;

        let disableBtn = !_selectedStudies.length > 0;
        let tempObj = {
            ProductPreferenceAnalysis: disableBtn,
            preferenceAnalysis: disableBtn,
            fragranceAssociation: disableBtn,
            agreementkpi: disableBtn,
        };
        setBtnDisabled(tempObj);
    }, [selectedStudy]);

    useEffect(() => {
        async function fetchAdditionalAnalysisData() {
            try {
                const selectedStudyTypeIds = [...new Set(selectedStudy.map((datum) => datum.study_type))].join(",");
                const selectedStudyTypeMethodIds = [...new Set(selectedStudy.map((datum) => datum.study_type_method))].join(",");
                let url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/all/?archive=false&uniq=true&variable_category=${encodeURIComponent("Fragrance Metrics Analysis")}`;
                if (productCategoryId) {
                    url += `&product_category=${productCategoryId}&study_type=${selectedStudyTypeIds}&study_type_method=${selectedStudyTypeMethodIds}`;
                }
                setIsAdditionalAnalysisDataLoading(true);
                await api
                    .get(url)
                    .then((res) => {
                        setAdditionalAnalysisData(res.data);
                        setIsAdditionalAnalysisDataLoading(false);
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsAdditionalAnalysisDataLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsAdditionalAnalysisDataLoading(false);
            }
        }

        async function fetchOverallAnalysisData() {
            try {
                const selectedStudyTypeIds = [...new Set(selectedStudy.map((datum) => datum.study_type))].join(",");
                const selectedStudyTypeMethodIds = [...new Set(selectedStudy.map((datum) => datum.study_type_method))].join(",");
                let url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/all/?archive=false&uniq=true&variable_category=${encodeURIComponent("Fragrance Metrics Analysis")}`;
                if (productCategoryId) {
                    url += `&product_category=${productCategoryId}&study_type=${selectedStudyTypeIds}&study_type_method=${selectedStudyTypeMethodIds}`;
                }
                setIsOverallAnalysisDataLoading(true);
                await api
                    .get(url)
                    .then((res) => {
                        setOverallAnalysisData(res.data);
                        setIsOverallAnalysisDataLoading(false);
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsOverallAnalysisDataLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsOverallAnalysisDataLoading(false);
            }
        }

        if (selectedStudy.length === 0) {
            navigate("/studies");
        } else {
            fetchAdditionalAnalysisData();
            fetchOverallAnalysisData();
        }
    }, [selectedStudy, dispatch, navigate, productCategoryId]);

    useEffect(() => {
        if (selectedStudy.length === 0) {
            navigate("/studies");
        } else {
            navigate(`/fragrance-metrics-analysis/express-clt/${page[selectedTab]}`, { replace: true });
        }
    }, [selectedTab, navigate, selectedStudy]);

    useEffect(() => {
        if (!currentAnalysisResult || currentAnalysisResult.length === 0) {
            setShowFragranceLikingNote(false);
            return;
        }
        const hasFragranceLiking = currentAnalysisResult.some((row) => row.variable === "R_OO_Frag_10pt" || (row.scores && row.scores.some((score) => score.backendVariable === "R_OO_Frag_10pt")));
        setShowFragranceLikingNote(hasFragranceLiking);
    }, [currentAnalysisResult]);

    const getTab = (dataAvailable, label, seq) => {
        if (dataAvailable) {
            return (
                <Tab
                    className="tab-labels"
                    iconPosition="start"
                    icon={<LineAxisOutlinedIcon />}
                    label={label}
                    {...a11yProps(seq)}
                />
            );
        } else {
            return (
                <Tooltip title="No data present">
                    {/* Wrap in span to avoid tooltip suppression on disabled */}
                    <span>
                        <Tab
                            className="tab-labels"
                            iconPosition="start"
                            icon={<LineAxisOutlinedIcon />}
                            label={label}
                            disabled={true}
                            {...a11yProps(seq)}
                        />
                    </span>
                </Tooltip>
            );
        }
    };

    return (
        <>
            {(isAdditionalAnalysisDataLoading || isOverallAnalysisDataLoading) && <Loader />}
            <Header
                title={headerTitle}
                isStudySelected={true}
            />
            <div
                className="ms-4 me-4 mb-0 mt-3 background-color-white box-shadow--2 d-flex"
                style={{
                    borderRadius: "5px",
                }}
            >
                <FiltersHeaderLabel multipleLabel={true} />
            </div>

            <div
                style={{
                    minHeight: "calc(100vh - 170px)",
                }}
                className="full-container m-4 background-color-white  box-shadow--full-container"
            >
                <div
                    className=""
                    style={{
                        width: 150,
                        display: "flex",
                        alignItems: "center",
                        position: "absolute",
                        zIndex: 10,
                    }}
                >
                    <Link
                        to="/studies"
                        onClick={(e) => {
                            if (window.history.length > 2) {
                                e.preventDefault();
                                navigate(-1);
                            }
                        }}
                    >
                        <div
                            className="p-2 liner-light-blue-background color-white"
                            style={{
                                borderRadius: "50%",
                                position: "relative",
                                top: -15,
                                left: -15,
                                cursor: "pointer",
                            }}
                        >
                            <span>
                                <ArrowBackIcon className="color-white" />
                            </span>
                        </div>
                    </Link>
                </div>

                <Tabs
                    sx={{
                        "& .MuiTabs-indicator": {
                            display: "none", // Hide the indicator
                        },
                    }}
                    value={selectedTab}
                    onChange={handleChange}
                    centered
                    aria-label="basic tabs example"
                    className="tabs-container tabs-toggle"
                >
                    {getTab(navigationAvailability["fragrance_metrics"], "Overall Analysis", 0)}
                    {getTab(navigationAvailability["fragrance_metrics"], "Additional Analysis", 1)}
                </Tabs>
                <CustomTabPanel
                    className="overall-kip-tab"
                    value={selectedTab}
                    index={0}
                >
                    {Object.keys(overallAnalysisData).length !== 0 && (
                        <FragrancePerformanceTab
                            selectedStudy={selectedStudy}
                            dataMappingData={overallAnalysisData}
                        />
                    )}
                </CustomTabPanel>
                <CustomTabPanel
                    className="overall-kip-tab"
                    value={selectedTab}
                    index={1}
                >
                    {additionalAnalysisData.length !== 0 && (
                        <AditionalAttributeTab
                            selectedStudy={selectedStudy}
                            dataMappingData={additionalAnalysisData}
                        />
                    )}
                </CustomTabPanel>
                {showFragranceLikingNote && (
                    <div
                        className="ms-4 color-grey"
                        style={{
                            fontSize: "12px",
                            position: "relative",
                            bottom: "-30px",
                        }}
                    >
                        {selectedStudy.length > 1 ? "Note: Products are grouped by study reference and ordered by descending values of 'Fragrance Liking' within each study" : "Note: The product order is determined based on the descending values of 'Fragrance Liking'"}
                    </div>
                )}
                <div
                    style={{
                        position: "relative",
                        top: "5px",
                        marginTop: "30px",
                    }}
                >
                    <MainNavigation
                        buttons={btnArr}
                        btnDisabledObj={btnDisabled}
                        isFragrance={true}
                    />
                </div>
            </div>
        </>
    );
}

// Typechecking props for the FragranceOverallKpi
FragranceMetricsExpressCLT.propTypes = {
    headerTitle: PropTypes.string,
};

export default FragranceMetricsExpressCLT;
